import CollectionStatusBadge from '@boViews/shared/CollectionStatusBadge';
import { faArrowLeft, faCloudArrowUp, faFileLines, faLockKeyhole, faPlus, faTrash, faUsers } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import ButtonSelect from '@sharedComponents/ButtonSelect';
import FlexBox from '@sharedComponents/FlexBox';
import TabBar from '@sharedComponents/TabBar';
import Tag from '@sharedComponents/Tag';
import Typography from '@sharedComponents/Typography';
import { displayText, parseI18n, REFERENCE_LANGUAGE } from '@src/i18n';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RESTRICTED, scopeCount } from './accessRules';
import AccessSection from './AccessSection';
import { toFormValues, toPayload } from './collectionForm';
import InformationsTab from './InformationsTab';
import TestsTab from './TestsTab';

const INFORMATION_TAB = 0;
const TESTS_TAB = 1;

/**
 * Page of an existing collection: header with the scope badge, "Information" tab with the access section, and
 * "Tests" tab (a collection is created from the side panel of the list). `overrides` are the client and platform
 * override rows of the collection. `onSave(payload)` resolves to the saved collection, or to null when
 * the save failed; `onChangeVisibility(visibility)` applies a confirmed scope; `searchClients` and
 * `onAddTarget` feed the search that adds a client or a platform to the collection (see TargetSearch);
 * `onSaveOverride`, `onRemoveOverride`, `loadClient`, `onAddTargets` and `onTransferOverride` change the
 * override rows (see AccessSection); `levels` are the levels of the tests; `onSearchTests`, `onAddTest`,
 * `onCreateTest`, `onRemoveTest` and `pendingTestReferences` feed the Tests tab (see TestsTab), which shows
 * `collection.tests`; `onDirtyChange` reports whether the form
 * holds unsaved changes; `onPublish` is called when "Publish" is picked in the actions menu. Both tabs stay
 * mounted, only the active one showing: a switch of tab keeps the unsaved changes, the requests in flight and
 * the test search.
 */
const CollectionDetail = ({
    collection,
    languages = [],
    levels = [],
    tagsOptions = {},
    overrides = [],
    onSave = async () => null,
    onChangeVisibility = async () => {},
    searchClients,
    onAddTarget,
    onSaveOverride,
    onRemoveOverride,
    loadClient,
    onAddTargets,
    onTransferOverride,
    onSearchTests,
    onAddTest,
    onCreateTest,
    onRemoveTest,
    pendingTestReferences = [],
    onPublish = () => {},
    onDelete = () => {},
    onBack = () => {},
    onDirtyChange = () => {},
} = {}) => {
    const { t } = useTranslation('bo');
    const methods = useForm({ defaultValues: toFormValues(collection, tagsOptions, languages) });
    const {
        getValues,
        setError,
        reset,
        formState: { isDirty },
    } = methods;
    const [activeLanguage, setActiveLanguage] = useState(REFERENCE_LANGUAGE);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [activeTab, setActiveTab] = useState(INFORMATION_TAB);

    useEffect(() => {
        onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const save = async () => {
        const values = getValues();
        if (!(values.name?.[REFERENCE_LANGUAGE] ?? '').trim()) {
            setActiveLanguage(REFERENCE_LANGUAGE);
            setError(`name.${REFERENCE_LANGUAGE}`, { type: 'required', message: t('The French name is required') });
            return;
        }
        setSaving(true);
        const saved = await onSave(toPayload(values, collection, tagsOptions)).catch(() => null);
        setSaving(false);
        // keepFieldsRef: the inputs must stay registered, as their memoized ref callbacks do not run again after a reset.
        if (saved) reset(toFormValues(saved, tagsOptions, languages), { keepFieldsRef: true });
    };

    // The actions menu shows a loader and stays closed until the publication settles.
    const publish = async () => {
        setPublishing(true);
        await Promise.resolve(onPublish()).catch(() => {});
        setPublishing(false);
    };

    // Legacy collections may lack a French name: the first available one is shown instead.
    const title = displayText(parseI18n(collection.name));
    // Only a DRAFT collection can be published: a published one goes back to DRAFT once modified.
    const publishable = collection.status === 'DRAFT';
    const scopeBadgeCount = scopeCount(overrides, collection.visibility);
    // Panel of a tab: hidden rather than unmounted when its tab is not active.
    const panelStyle = (tab) => ({ display: activeTab === tab ? 'flex' : 'none' });
    const tabs = [
        { label: t('Information'), selected: activeTab === INFORMATION_TAB },
        { label: t('Tests ({{total}})', { total: collection.tests.length }), selected: activeTab === TESTS_TAB },
    ];

    return (
        <FlexBox flexDirection="column" gap="m" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <Button
                ghost
                noPadding
                icon={faArrowLeft}
                label={t('Back to collections')}
                action={onBack}
                style={{ alignSelf: 'flex-start' }}
            />

            <FlexBox justifyContent="space-between" alignItems="flex-start" gap="s">
                <FlexBox flexDirection="column" gap="xs" alignItems="flex-start">
                    <FlexBox gap="s" alignItems="center">
                        <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                            {title}
                        </Typography>
                        <CollectionStatusBadge status={collection.status} />
                    </FlexBox>
                    <FlexBox gap="xs" alignItems="center" flexWrap="wrap">
                        <Tag color="neutral" icon={faFileLines} iconPosition="left">
                            {t('{{count}} test(s)', { count: collection.tests.length })}
                        </Tag>
                        {collection.visibility === RESTRICTED ? (
                            <Tag color="warning" icon={faLockKeyhole} iconPosition="left">
                                {t('Restricted · {{count}} authorized', { count: scopeBadgeCount })}
                            </Tag>
                        ) : (
                            <Tag color="lavender" icon={faUsers} iconPosition="left">
                                {t('Public · {{count}} override(s)', { count: scopeBadgeCount })}
                            </Tag>
                        )}
                    </FlexBox>
                </FlexBox>
                <ButtonSelect
                    icon={faPlus}
                    defaultValue={{ label: t('Actions') }}
                    loading={publishing}
                    options={[
                        {
                            label: t('Publish'),
                            icon: faCloudArrowUp,
                            action: publish,
                            disabled: !publishable,
                            title: publishable ? undefined : t('Already published: modify the collection to publish it again.'),
                        },
                        { label: t('Delete the collection'), icon: faTrash, action: onDelete, danger: true },
                    ]}
                />
            </FlexBox>

            <FlexBox additionnalStyles={{ borderBottom: '1px solid var(--border-color-neutral-tertiary)' }}>
                <TabBar tabs={tabs} onClick={setActiveTab} />
            </FlexBox>

            <FlexBox flexDirection="column" gap="m" additionnalStyles={panelStyle(INFORMATION_TAB)}>
                <FormProvider {...methods}>
                    <InformationsTab
                        languages={languages}
                        tagsOptions={tagsOptions}
                        activeLanguage={activeLanguage}
                        saving={saving}
                        onLanguageChange={setActiveLanguage}
                        onSave={save}
                    />
                </FormProvider>

                <AccessSection
                    collection={collection}
                    visibility={collection.visibility}
                    overrides={overrides}
                    languages={languages}
                    onChangeVisibility={onChangeVisibility}
                    searchClients={searchClients}
                    onAddTarget={onAddTarget}
                    onSaveOverride={onSaveOverride}
                    onRemoveOverride={onRemoveOverride}
                    loadClient={loadClient}
                    onAddTargets={onAddTargets}
                    onTransferOverride={onTransferOverride}
                />
            </FlexBox>
            <FlexBox flexDirection="column" additionnalStyles={panelStyle(TESTS_TAB)}>
                <TestsTab
                    tests={collection.tests}
                    languagesOptions={languages}
                    levelsOptions={levels}
                    onSearch={onSearchTests}
                    onAdd={onAddTest}
                    onCreate={onCreateTest}
                    onRemove={onRemoveTest}
                    pendingReferences={pendingTestReferences}
                />
            </FlexBox>
        </FlexBox>
    );
};

export default CollectionDetail;
